/**
 * CommunityModeratorsPanel — admin-only UI to grant/revoke moderator
 * status on the ADI and PDI community spaces.
 *
 * Minimal by design:
 *   - Paste user id, pick space, click Grant
 *   - Lists current moderators with a Revoke button
 *   - No search UI (keeping MVP small; admin can grab ids from other panels)
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, Trash2, UserPlus } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SPACES = ["adi", "pdi"];

export default function CommunityModeratorsPanel() {
  const [rosters, setRosters] = useState({ adi: [], pdi: [] });
  const [loading, setLoading] = useState(true);
  const [space, setSpace] = useState("adi");
  const [userId, setUserId] = useState("");
  const [granting, setGranting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.all(
        SPACES.map((s) =>
          axios
            .get(`${API_URL}/api/community/moderators/${s}`, { withCredentials: true })
            .then((r) => [s, r.data?.moderators || []])
            .catch(() => [s, []]),
        ),
      );
      setRosters(Object.fromEntries(results));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const grant = async (e) => {
    e.preventDefault();
    if (!userId.trim()) return;
    setGranting(true);
    try {
      await axios.post(
        `${API_URL}/api/community/moderators/${space}/${userId.trim()}`,
        {},
        { withCredentials: true },
      );
      toast.success(`Moderator granted in ${space.toUpperCase()}`);
      setUserId("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't grant moderator");
    } finally {
      setGranting(false);
    }
  };

  const revoke = async (spaceKey, uid) => {
    if (!window.confirm(`Revoke moderator in ${spaceKey.toUpperCase()}?`)) return;
    try {
      await axios.delete(
        `${API_URL}/api/community/moderators/${spaceKey}/${uid}`,
        { withCredentials: true },
      );
      toast.success("Moderator revoked");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't revoke");
    }
  };

  return (
    <div data-testid="admin-community-moderators" className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="p-4 border-b border-slate-200 flex items-center gap-3">
        <ShieldCheck className="w-5 h-5 text-emerald-600" />
        <h2 className="font-bold text-slate-900">Community Moderators</h2>
        <span className="text-xs text-slate-400 ml-auto">Admin only</span>
      </div>

      <form onSubmit={grant} className="p-4 flex flex-col sm:flex-row gap-2 border-b border-slate-200">
        <select
          value={space}
          onChange={(e) => setSpace(e.target.value)}
          data-testid="mod-grant-space"
          className="text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2"
        >
          <option value="adi">ADI space</option>
          <option value="pdi">PDI space</option>
        </select>
        <input
          type="text"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          placeholder="User id (24-char hex)"
          data-testid="mod-grant-user-id"
          className="flex-1 text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400"
        />
        <button
          type="submit"
          disabled={granting || !userId.trim()}
          data-testid="mod-grant-submit"
          className="inline-flex items-center gap-1.5 bg-indigo-600 text-white font-bold text-sm rounded-lg px-3 py-2 hover:bg-indigo-700 disabled:bg-indigo-300"
        >
          <UserPlus className="w-4 h-4" />
          Grant
        </button>
      </form>

      <div className="p-4 space-y-4">
        {loading ? (
          <div className="text-center text-xs text-slate-400 py-4">Loading…</div>
        ) : (
          SPACES.map((s) => (
            <div key={s} data-testid={`mod-roster-${s}`}>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-2">
                {s.toUpperCase()} Space ({rosters[s]?.length || 0})
              </p>
              {(rosters[s] || []).length === 0 ? (
                <p className="text-xs text-slate-400">No moderators yet.</p>
              ) : (
                <ul className="space-y-1">
                  {rosters[s].map((m) => (
                    <li
                      key={m.user_id}
                      data-testid={`mod-row-${m.user_id}`}
                      className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2"
                    >
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                        <span className="text-sm font-semibold">{m.name}</span>
                        <span className="text-[10px] uppercase tracking-wider text-slate-400">
                          {m.role}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => revoke(s, m.user_id)}
                        data-testid={`mod-revoke-${m.user_id}`}
                        className="text-[11px] font-semibold text-rose-600 hover:text-rose-800 inline-flex items-center gap-1 px-2 py-1 rounded-md"
                      >
                        <Trash2 className="w-3 h-3" />
                        Revoke
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
